// Resilience Dashboard — dependency health, revocation SLO, maintenance
// toggle. PAM Epic 12.
//
// OIDCPAM Gate-6 Phase 5e: migrated off MUI onto
// @devnw/cyber-iam/iam/ResilienceDashboard — the exact parity component
// built from this page. The component owns the dependency-health table, the
// revocation-SLO table, and the maintenance-toggle inline two-phase confirm;
// this page still owns both API calls, the wire-mapping
// (../api/resilienceMapping — FIXES a latent snake_case/camelCase bug, see
// that file's header), tenant gating, and wiring onSetMaintenance/onRefresh
// to the right mutations. POLLING/RELOADING IS A HOST CONCERN — no
// setInterval here, matching the pre-migration page exactly.
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import IamResilienceDashboard from '@devnw/cyber-iam/iam/ResilienceDashboard/ResilienceDashboard'
import type {
  ResilienceStatus,
  SloDashboard,
} from '@devnw/cyber-iam/iam/types'
import { useAdminGenClient } from '../api/genClient'
import { useActiveTenant } from '../layout/TenantSwitcher'
import {
  toIamResilienceStatus,
  toIamSloDashboard,
  type WireResilienceStatus,
  type WireSloDashboard,
} from '../api/resilienceMapping'

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Request failed'
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('lg')};
`

const MutedText = styled.p`
  margin: 0;
  color: ${({ theme }) => asUnified(theme).text.secondary};
`

// ---------------------------------------------------------------------------
// ResilienceDashboard (accepts tenantId prop — testable without router context)
// ---------------------------------------------------------------------------

export const ResilienceDashboard = ({
  tenantId,
}: {
  tenantId: string
}): JSX.Element => {
  const client = useAdminGenClient()
  const [status, setStatus] = useState<ResilienceStatus | null>(null)
  const [slo, setSlo] = useState<SloDashboard | null>(null)
  const [loadingStatus, setLoadingStatus] = useState(false)
  const [loadingSlo, setLoadingSlo] = useState(false)
  const [errorStatus, setErrorStatus] = useState<string | undefined>(undefined)
  const [errorSlo, setErrorSlo] = useState<string | undefined>(undefined)

  const loadStatus = useCallback(async (): Promise<void> => {
    setLoadingStatus(true)
    setErrorStatus(undefined)
    try {
      const data = await client.adminPAMResilienceStatus({ tenantId })
      setStatus(toIamResilienceStatus(data as unknown as WireResilienceStatus))
    } catch (e: unknown) {
      setErrorStatus(errMessage(e))
    } finally {
      setLoadingStatus(false)
    }
  }, [client, tenantId])

  const loadSlo = useCallback(async (): Promise<void> => {
    setLoadingSlo(true)
    setErrorSlo(undefined)
    try {
      const data = await client.adminPAMSLODashboard({ tenantId })
      setSlo(toIamSloDashboard(data as unknown as WireSloDashboard))
    } catch (e: unknown) {
      setErrorSlo(errMessage(e))
    } finally {
      setLoadingSlo(false)
    }
  }, [client, tenantId])

  useEffect(() => {
    loadStatus()
      .then(() => {})
      .catch(() => {})
    loadSlo()
      .then(() => {})
      .catch(() => {})
  }, [loadStatus, loadSlo])

  const handleRefresh = useCallback((): void => {
    loadStatus()
      .then(() => {})
      .catch(() => {})
    loadSlo()
      .then(() => {})
      .catch(() => {})
  }, [loadStatus, loadSlo])

  const handleSetMaintenance = useCallback(
    async (enabled: boolean): Promise<void> => {
      // AdminPAMMaintenanceToggleReq is concretely typed as { enabled,
      // tenantId } — no opaque cast needed (unlike status/slo above).
      await client.adminPAMMaintenanceToggle({ tenantId, enabled })
      // Reload status to reflect server-side update (unchanged sequencing).
      await loadStatus()
    },
    [client, tenantId, loadStatus]
  )

  return (
    <IamResilienceDashboard
      testID="resilience-dashboard"
      status={status}
      slo={slo}
      onSetMaintenance={handleSetMaintenance}
      onRefresh={handleRefresh}
      loadingStatus={loadingStatus}
      loadingSlo={loadingSlo}
      statusError={errorStatus}
      sloError={errorSlo}
    />
  )
}

// ---------------------------------------------------------------------------
// ResilienceDashboardPage — page-level wrapper that pulls tenantId from context
// ---------------------------------------------------------------------------

export const ResilienceDashboardPage = (): JSX.Element => {
  const { tenant } = useActiveTenant()

  if (!tenant) {
    return (
      <PageContainer>
        <MutedText>
          Select a tenant to view the Resilience dashboard.
        </MutedText>
      </PageContainer>
    )
  }

  return <ResilienceDashboard tenantId={tenant} />
}
